import { useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApplicantDetails } from '../../../hooks/api/useApplicantDetails';
import { useGetStudentDocumentsQuery } from '../../../hooks/api/useGetStudentDocumentsQuery';
import { useVerifyDocumentMutation } from '../../../hooks/api/useVerifyDocumentMutation';
import {
  App,
  Avatar,
  Button as AntButton,
  Card,
  Col,
  Collapse,
  CollapseProps,
  Divider,
  Flex,
  Modal,
  Row,
  Space,
  Spin,
  Table,
  Typography,
} from 'antd';
import { Form, Formik, FormikValues } from 'formik';
import { formatFullDate } from '../../../utils/formatDate';
import styles from '../styles.module.scss';
import { useDownload } from '../../../hooks/useDownload';
import { ColumnsType } from 'antd/es/table';
import { ReactComponent as Back } from '../../../assets/arrow.svg';
import { ReactComponent as DownloadIcon } from '../../../assets/download.svg';
import { ReactComponent as JHULogo } from '../../../assets/James-Hope-University-Logo.svg';
import { routes } from '../../../routes';
import Input from '../../../custom/input/input';
import Select from '../../../custom/select/select';
import Button from '../../../custom/button/button';
import { useReactToPrint } from "react-to-print";
import { getApplicantReferences, updateApplicantReferenceStatus } from '../../userManagement/request';
import * as Yup from 'yup';
import { ReactComponent as LogoIcon } from "./assets/James-Hope-University-Logo.svg";


const { Text, Title } = Typography;

const PRINT_STYLES = `
  @page { size: A4 portrait; margin: 12mm 14mm; }
  * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; box-sizing: border-box; }
  html, body { margin: 0 !important; padding: 0 !important; height: auto !important; min-height: 0 !important; overflow: visible !important; }
  body { font-size: 10px !important; }
  .ant-space { display: block !important; width: 100% !important; }
  .ant-space-item { display: block !important; width: 100% !important; margin-bottom: 5px !important; }
  .ant-card { border: 1px solid #d1d5db !important; box-shadow: none !important; border-radius: 4px !important; margin-bottom: 5px !important; break-inside: avoid; page-break-inside: avoid; }
  .ant-card-head { min-height: 28px !important; padding: 0 10px !important; font-size: 11px !important; font-weight: 700 !important; }
  .ant-card-body { padding: 8px 10px !important; }
  .ant-table-thead > tr > th, .ant-table-tbody > tr > td { padding: 3px 6px !important; font-size: 9px !important; }
  .ant-table-thead > tr > th:last-child, .ant-table-tbody > tr > td:last-child { display: none !important; }
  .ant-btn { display: none !important; }
  .ant-pagination { display: none !important; }
  .ant-divider { margin: 4px 0 !important; }
  .ant-collapse-header { padding: 4px 10px !important; font-size: 10px !important; font-weight: 600 !important; }
  .ant-collapse-expand-icon { display: none !important; }
  .ant-collapse-content { border-top: 1px solid #e5e7eb !important; }
  .ant-typography { font-size: 10px !important; }
  h4.ant-typography { font-size: 12px !important; margin-bottom: 0 !important; }
  .ant-avatar { width: 60px !important; height: 60px !important; line-height: 60px !important; }
  a { color: #1f2937 !important; text-decoration: none !important; }
`;

const ViewApplicant = () => {
  const { id: applicantId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const printRef = useRef<HTMLDivElement>(null);
  const [isExpandedForPrint, setIsExpandedForPrint] = useState(false);

  const {
    personalInfo,
    studyDetails,
    institutions,
    workHistory,
    qualifications,
    jambResult,
    personalInfoLoading,
    studyDetailsLoading,
    institutionLoading,
    workHistoryLoading,
    qualificationsLoading,
    jambResultLoading,
    personalInfoError,
    studyDetailsError,
    institutionError,
    workHistoryError,
    qualificationsError,
    jambResultError,
  } = useApplicantDetails(applicantId || '');
  const {
    studentDocumentsData,
    isStudentDocumentsLoading,
    isStudentDocumentsError,
    studentDocumentsErrorMessage,
  } = useGetStudentDocumentsQuery(applicantId || '');

  const handlePrint = useReactToPrint({
    contentRef: printRef,
    pageStyle: PRINT_STYLES,
    documentTitle: `Applicant_${personalInfo?.applicationNumber || applicantId}`,
    onBeforePrint: () =>
      new Promise<void>((resolve) => {
        setIsExpandedForPrint(true);
        setTimeout(resolve, 350);
      }),
    onAfterPrint: () => setIsExpandedForPrint(false),
  });

  return (
    <div className={styles.page}>
      <Flex align="center" gap="8px" className={styles.headerBar}>
        <AntButton type="text" onClick={() => navigate(routes.userMgt.users)}>
          <Back />
        </AntButton>
        <h3>Applicant Details</h3>
        <AntButton
          style={{ marginLeft: 'auto', backgroundColor: '#8F0024', color: 'white' }}
          onClick={handlePrint}
        >
          Print Applicant Detail
        </AntButton>
      </Flex>
      <Divider style={{ margin: '12px 0 20px' }} />

      <div ref={printRef} style={{ margin: 0, padding: 0 }}>
        {isExpandedForPrint && personalInfo && (
          <div style={{ textAlign: 'center', marginBottom: 10, paddingBottom: 8, borderBottom: '2px solid #8F0024' }}>
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <JHULogo style={{ height: 64, width: 'auto' }} />
            </div>
            <h2 style={{ fontSize: 14, margin: '0 0 2px', color: '#abda0f', fontWeight: 700 }}>
              PETROLEUM TRAINING INSTITUTE
            </h2>
            <h3 style={{ fontSize: 12, margin: '0 0 5px', fontWeight: 600, color: '#1f2937' }}>
              Applicant Profile
            </h3>
            <p style={{ fontSize: 10, margin: 0, color: '#4b5563' }}>
              <strong>{personalInfo.fName} {personalInfo.mNane ? `${personalInfo.mNane} ` : ''}{personalInfo.lName}</strong>
              &nbsp;&nbsp;|&nbsp;&nbsp;App No: {personalInfo.applicationNumber}
              &nbsp;&nbsp;|&nbsp;&nbsp;Email: {personalInfo.email}
            </p>
          </div>
        )}

        <Space direction="vertical" size={16} className={styles.sectionStack}>
          {personalInfoLoading ? (
            <SectionState title="Bio-data" loading />
          ) : personalInfoError ? (
            <SectionState title="Bio-data" error="Unable to load biodata." />
          ) : (
            personalInfo?.fName && <Biodata personalInfo={personalInfo} />
          )}

          {studyDetailsLoading ? (
            <SectionState title="Study Details" loading />
          ) : studyDetailsError ? (
            <SectionState title="Study Details" error="Unable to load study details." />
          ) : (
            studyDetails?.programName && <StudyDetailsCard details={studyDetails} />
          )}

          {institutionLoading ? (
            <SectionState title="Institutions" loading />
          ) : institutionError ? (
            <SectionState title="Institutions" error="Unable to load institutions." />
          ) : (
            institutions && institutions.length > 0 && (
              <Institution institutions={institutions} allExpanded={isExpandedForPrint} />
            )
          )}

          {qualificationsLoading ? (
            <SectionState title="Qualifications" loading />
          ) : qualificationsError ? (
            <SectionState title="Qualifications" error="Unable to load qualifications." />
          ) : (
            qualifications && qualifications.length > 0 && (
              <QualificationDetails qualifications={qualifications} allExpanded={isExpandedForPrint} />
            )
          )}

          {jambResultLoading ? (
            <SectionState title="JAMB Result" loading />
          ) : jambResultError ? (
            <SectionState title="JAMB Result" error="Unable to load JAMB result." />
          ) : (
            jambResult?.id && <JambResultCard jambResult={jambResult} />
          )}

          {isStudentDocumentsLoading ? (
            <SectionState title="Uploaded Documents" loading />
          ) : isStudentDocumentsError ? (
            <SectionState title="Uploaded Documents" error={studentDocumentsErrorMessage} />
          ) : (
            studentDocumentsData &&
            studentDocumentsData.length > 0 && (
              <StudentDocumentsCard applicantId={applicantId || ''} studentDocuments={studentDocumentsData} />
            )
          )}

          {workHistoryLoading ? (
            <SectionState title="Work History" loading />
          ) : workHistoryError ? (
            <SectionState title="Work History" error="Unable to load work history." />
          ) : (
            workHistory && workHistory.length > 0 && (
              <WorkHistory workHistory={workHistory} allExpanded={isExpandedForPrint} />
            )
          )}

          {applicantId && <ApplicantReferences applicantId={applicantId} />}
        </Space>
      </div>
    </div>
  );
};

const InfoItem = ({ label, value }: { label: string; value?: React.ReactNode }) => (
  <Col xs={24} sm={12} lg={8} className={styles.infoItem}>
    <Text className={styles.infoLabel}>{label}</Text>
    <div className={styles.infoValue}>{value || 'N/A'}</div>
  </Col>
);

const SectionState = ({ title, loading, error }: { title: string; loading?: boolean; error?: string }) => (
  <Card title={title} className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
    {loading ? <Spin /> : <Text type="danger">{error}</Text>}
  </Card>
);

const Biodata = ({ personalInfo }: { personalInfo: PersonalInfo }) => (
  <Card title="Bio-data" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
    <Flex gap="16px" align="center" className={styles.bioHeader}>
      <AvatarBlock personalInfo={personalInfo} />
      <div className={styles.identityBlock}>
        <Title level={4} className={styles.noMargin}>
          {personalInfo?.fName} {personalInfo?.mNane ? `${personalInfo?.mNane} ` : ''}
          {personalInfo?.lName}
        </Title>
        <Text type="secondary">Applicant No: {personalInfo?.applicationNumber}</Text>
      </div>
    </Flex>
    <Divider />
    <Row gutter={[16, 12]} className={styles.infoGrid}>
      <InfoItem label="First name" value={personalInfo?.fName} />
      <InfoItem label="Middle name" value={personalInfo?.mNane} />
      <InfoItem label="Last name" value={personalInfo?.lName} />
      <InfoItem label="Gender" value={personalInfo?.gender} />
      <InfoItem label="Date of Birth" value={formatFullDate(personalInfo?.dateOfBirth)} />
      <InfoItem label="Email" value={personalInfo?.email} />
      <InfoItem label="Phone Number" value={personalInfo?.phoneNo} />
      <InfoItem label="Address" value={personalInfo?.address} />
      <InfoItem label="Country" value={personalInfo?.countryName} />
      <InfoItem label="State of Origin" value={personalInfo?.stateOfOrigin} />
      <InfoItem label="LGA" value={personalInfo?.lga} />
    </Row>
  </Card>
);

const StudyDetailsCard = ({ details }: { details: StudyDetails }) => {
  return (
    <Card title="Application Details" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
      <Row gutter={[16, 12]} className={styles.infoGrid}>
        <InfoItem label="Application No" value={details?.applicationNumber} />
        <InfoItem label="Program" value={details?.programName} />
        <InfoItem label="Degree Type" value={details?.programTypeName} />
        <InfoItem label="Batch" value={details?.applicationBatchName} />
        <InfoItem label="Mode of Study" value={details?.modeOfStudyName} />
        <InfoItem label="Mode of Entry" value={details?.modeOfEntryName} />
      </Row>
    </Card>
  );
};

const AvatarBlock = ({ personalInfo }: { personalInfo: PersonalInfo }) => {
  const { downloadFile, isDownloading } = useDownload();
  const photoUrl = personalInfo?.imageUrl;

  return (
    <Space direction="vertical" align="center" size={8} className={styles.avatarBlock}>
      <Avatar size={96} shape="square" src={photoUrl} className={styles.avatar}>
        {personalInfo?.fName?.[0] || 'A'}
      </Avatar>
      <AntButton
        size="small"
        type="default"
        disabled={!photoUrl}
        loading={isDownloading}
        onClick={() => photoUrl && downloadFile(photoUrl, 'applicant_photo.jpg')}
      >
        Download photo
      </AntButton>
    </Space>
  );
};

const Institution = ({ institutions, allExpanded }: { institutions: Institution[]; allExpanded?: boolean }) => {
  const { downloadFile, isDownloading } = useDownload();

  const items: CollapseProps['items'] = institutions.map((institution) => ({
    key: institution.id,
    label: institution.institutionName,
    children: (
      <Row gutter={[16, 12]} className={styles.infoGrid}>
        <InfoItem label="Institution Name" value={institution.institutionName} />
        <InfoItem label="Discipline" value={institution.discipline} />
        <InfoItem label="CGPA" value={institution.cgpa} />
        <InfoItem
          label="Duration"
          value={`${formatFullDate(institution.startDate)} - ${formatFullDate(institution.endDate)}`}
        />
        <Col span={24}>
          <Flex gap="12px" wrap="wrap" className={styles.listActions}>
            <a className={styles.link} href={institution.certificateUrl} target="_blank" rel="noopener noreferrer">
              View Certificate
            </a>
            <Button
              type="button"
              variant="text"
              text="Download Certificate"
              isLoading={isDownloading}
              onClick={() => downloadFile(institution.certificateUrl, 'certificate.pdf')}
              iconBefore={<DownloadIcon width={16} height={16} />}
            />
          </Flex>
        </Col>
      </Row>
    ),
  }));

  const allKeys = institutions.map((i) => i.id);

  return (
    <Card title="Institutions" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
      <Collapse items={items} size="small" activeKey={allExpanded ? allKeys : undefined} />
    </Card>
  );
};

const QualificationDetails = ({
  qualifications,
  allExpanded,
}: {
  qualifications: Qualification[];
  allExpanded?: boolean;
}) => {
  const columns: ColumnsType<Subject> = [
    {
      key: 'appNo',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'subject',
      title: 'Subject',
      dataIndex: 'subject',
    },
    {
      key: 'grade',
      title: 'Grade',
      dataIndex: 'grade',
    },
  ];

  const items: CollapseProps['items'] = qualifications.map((qualification) => ({
    key: qualification.id,
    label: qualification.qualificationType,
    children: (
      <Table
        dataSource={qualification.subjects}
        columns={columns}
        rowKey={(record) => record.id}
        scroll={{ x: true }}
        pagination={false}
      />
    ),
  }));

  const allKeys = qualifications.map((q) => q.id);

  return (
    <Card title="Qualifications" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
      <Collapse items={items} size="small" activeKey={allExpanded ? allKeys : undefined} />
    </Card>
  );
};

const JambResultCard = ({ jambResult }: { jambResult: JambResult }) => {
  const { downloadFile, isDownloading } = useDownload();

  const columns: ColumnsType<JambSubject> = [
    {
      key: 'sn',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'subjectName',
      title: 'Subject',
      dataIndex: 'subjectName',
    },
    {
      key: 'score',
      title: 'Score',
      dataIndex: 'score',
    },
  ];

  return (
    <Card title="JAMB Result" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
      <Row gutter={[16, 12]} className={styles.infoGrid}>
        <InfoItem label="Exam Number" value={jambResult.examNumber} />
        <InfoItem label="Exam Year" value={jambResult.examYear} />
        <InfoItem label="Total Score" value={jambResult.totalScore} />
        <InfoItem label="Institution" value={jambResult.institutionShortName} />
      </Row>

      <Divider />

      <Table
        dataSource={jambResult.subjects}
        columns={columns}
        rowKey={(record) => record.id}
        pagination={false}
        scroll={{ x: true }}
      />

      {jambResult.jambResultUrl && (
        <>
          <Divider />
          <Flex gap="12px" wrap="wrap" className={styles.listActions}>
            <a className={styles.link} href={jambResult.jambResultUrl} target="_blank" rel="noopener noreferrer">
              View JAMB Result
            </a>
            <AntButton
              type="text"
              loading={isDownloading}
              onClick={() => downloadFile(jambResult.jambResultUrl, 'jamb_result.pdf')}
              icon={<DownloadIcon width={16} height={16} />}
            >
              Download JAMB Result
            </AntButton>
          </Flex>
        </>
      )}
    </Card>
  );
};

type VerifyDocumentFormValues = {
  isApproved: string;
  rejectionReason: string;
};

const StudentDocumentsCard = ({
  applicantId,
  studentDocuments,
}: {
  applicantId: string | number;
  studentDocuments: StudentDocument[];
}) => {
  const { downloadFile, isDownloading } = useDownload();
  const [selectedDocument, setSelectedDocument] = useState<StudentDocument | null>(null);
  const { verifyDocumentHandler, isLoading } = useVerifyDocumentMutation(applicantId);

  const verificationSchema = useMemo(
    () =>
      Yup.object().shape({
        isApproved: Yup.string().required('Verification status is required'),
        rejectionReason: Yup.string().when('isApproved', {
          is: 'false',
          then: (schema) => schema.trim().required('Rejection reason is required'),
          otherwise: (schema) => schema.optional(),
        }),
      }),
    [],
  );

  const handleCloseModal = () => setSelectedDocument(null);

  const columns: ColumnsType<StudentDocument> = [
    {
      key: 'sn',
      title: 'S/N',
      render: (_text, _record, index) => index + 1,
    },
    {
      key: 'documentName',
      title: 'Document',
      render: (_, record) => record.documentName || record.name || record.fileName || 'N/A',
    },
    {
      key: 'requiredDocumentName',
      title: 'Name',
      render: (_, record) => record.requiredDocumentName || record.allowedFileTypes || 'N/A',
    },
    {
      key: 'status',
      title: 'Status',
      render: (_, record) => record?.verificationStatus || (record.isUploaded ? 'Uploaded' : 'Pending'),
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => {
        const documentUrl = record.fileUrl || record.documentUrl;

        return (
          <Flex gap="12px" wrap="wrap" className={styles.listActions}>
            {documentUrl && (
              <a className={styles.link} href={documentUrl} target="_blank" rel="noopener noreferrer">
                View
              </a>
            )}
            {documentUrl && (
              <Button
                type="button"
                variant="text"
                text="Download"
                isLoading={isDownloading}
                onClick={() => downloadFile(documentUrl, record.fileName || 'student_document')}
                iconBefore={<DownloadIcon width={16} height={16} />}
              />
            )}
            <AntButton type="primary" ghost onClick={() => setSelectedDocument(record)}>
              Verify
            </AntButton>
          </Flex>
        );
      },
    },
  ];

  return (
    <>
      <Card title="Uploaded Documents" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
        <Table dataSource={studentDocuments} columns={columns} rowKey={(record) => record.id} scroll={{ x: true }} />
      </Card>

      <Modal
        open={Boolean(selectedDocument)}
        onCancel={handleCloseModal}
        footer={null}
        destroyOnClose
        title="Verify Document"
      >
        {selectedDocument && (
          <Formik<VerifyDocumentFormValues>
            initialValues={{
              isApproved:
                selectedDocument.status?.toLowerCase() === 'approved'
                  ? 'true'
                  : selectedDocument.status?.toLowerCase() === 'rejected'
                    ? 'false'
                    : '',
              rejectionReason: '',
            }}
            validationSchema={verificationSchema}
            onSubmit={async (values, { resetForm }) => {
              const isSuccessful = await verifyDocumentHandler({
                studentDocumentId: Number(selectedDocument.id),
                isApproved: values.isApproved === 'true',
                rejectionReason: values.isApproved === 'false' ? values.rejectionReason.trim() : '',
              });

              if (isSuccessful) {
                resetForm();
                handleCloseModal();
              }
            }}
          >
            {({ values }) => (
              <Form className={styles.verifyForm}>
                <Select
                  name="isApproved"
                  label="Verification Status"
                  placeholder="Select status"
                  options={[
                    <option key="approve" value="true">
                      Approve
                    </option>,
                    <option key="reject" value="false">
                      Reject
                    </option>,
                  ]}
                />
                <Input
                  name="rejectionReason"
                  label="Rejection Reason"
                  placeholder="Enter rejection reason"
                  type="textarea"
                  disabled={values.isApproved !== 'false'}
                />
                <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
                  <AntButton className={styles.modalActionButton} onClick={handleCloseModal}>
                    Cancel
                  </AntButton>
                  <Button
                    className={styles.modalActionButton}
                    type="submit"
                    text="Submit"
                    isLoading={isLoading}
                    disabled={isLoading}
                  />
                </Flex>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

const WorkHistory = ({ workHistory, allExpanded }: { workHistory: WorkHistory[]; allExpanded?: boolean }) => {
  const { downloadFile, isDownloading } = useDownload();
  const items: CollapseProps['items'] = workHistory.map((work) => ({
    key: work.id,
    label: work.companyName,
    children: (
      <Row gutter={[16, 12]} className={styles.infoGrid}>
        <InfoItem label="Company Name" value={work.companyName} />
        <InfoItem label="Position" value={work.position} />
        <InfoItem
          label="Duration"
          value={`${formatFullDate(work.startDate)} - ${
            work.endDate === '0001-01-01T00:00:00' ? 'Present' : formatFullDate(work.endDate)
          }`}
        />
        <Col span={24}>
          <Flex gap="12px" wrap="wrap" className={styles.listActions}>
            <a className={styles.link} href={work.employmentUrl} target="_blank" rel="noopener noreferrer">
              View Offer Letter
            </a>
            <AntButton
              type="text"
              loading={isDownloading}
              onClick={() => downloadFile(work.employmentUrl, 'employment_letter.pdf')}
              icon={<DownloadIcon width={16} height={16} />}
            >
              Download Offer Letter
            </AntButton>
          </Flex>
        </Col>
      </Row>
    ),
  }));

  const allKeys = workHistory.map((w) => w.id);

  return (
    <Card title="Work History" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
      <Collapse items={items} size="small" activeKey={allExpanded ? allKeys : undefined} />
    </Card>
  );
};

const ApplicantReferences = ({ applicantId }: { applicantId: string }) => {
   const [selectedReference, setSelectedReference] = useState<ReferenceData | null>(null);
  const { notification } = App.useApp();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['applicant-references', applicantId],
    queryFn: () => getApplicantReferences(applicantId),
  });

  const references: ReferenceData[] = Array.isArray((data?.data as any)?.records)
    ? (data?.data as any).records
    : [];

 const updateStatusMutation = useMutation({
  mutationFn: updateApplicantReferenceStatus, // ✅ Correct name
});

  const handleUpdate = async (values: FormikValues, resetForm: () => void) => {
    if (!selectedReference) return;
    try {
      await updateStatusMutation.mutateAsync(
        { id: selectedReference.id, status: Number(values.referenceStatus) },
        {
          onSuccess: (res) => {
            notification.success({ message: 'Success', description: res?.message });
            queryClient.refetchQueries({ queryKey: ['applicant-references', applicantId] });
            resetForm();
            setSelectedReference(null);
          },
        },
      );
    } catch (error: any) {
      notification.error({
        message: 'Error',
        description: error?.response?.data?.message,
      });
    }
  };

  const validationSchema = useMemo(
    () =>
      Yup.object().shape({
        referenceStatus: Yup.number().required('Reference status is required'),
      }),
    [],
  );

  const columns: ColumnsType<ReferenceData> = [
    { key: 'referenceNumber', title: 'Reference Number', dataIndex: 'referenceNumber' },
    { key: 'refereeName', title: 'Referee Name', dataIndex: 'refereeName' },
    { key: 'refereeEmail', title: 'Referee Email', dataIndex: 'refereeEmail' },
    { key: 'refereeCategoryString', title: 'Category', dataIndex: 'refereeCategoryString' },
    {
      key: 'referenceStatus',
      title: 'Status',
      dataIndex: 'referenceStatus',
      render: (status: number) => {
        const map: Record<number, string> = { 0: 'Pending', 1: 'Sent To Referee', 2: 'Received Reference Letter' };
        return map[status] ?? status;
      },
    },
    {
      key: 'action',
      title: 'Action',
      render: (_, record) => (
        <AntButton type="primary" ghost onClick={() => setSelectedReference(record)}>
          Edit
        </AntButton>
      ),
    },
  ];

  if (isLoading) return <SectionState title="References" loading />;
  if (isError) return <SectionState title="References" error="Unable to load references." />;

  return (
    <>
      <Card title="References" className={styles.sectionCard} styles={{ body: { padding: 16 } }}>
        <Table
          dataSource={references}
          columns={columns}
          rowKey={(record) => record.id}
          scroll={{ x: true }}
          locale={{ emptyText: 'No references found' }}
        />
      </Card>

      <Modal
        open={Boolean(selectedReference)}
        onCancel={() => setSelectedReference(null)}
        footer={null}
        destroyOnClose
        title="Edit Reference"
      >
        {selectedReference && (
          <Formik
            initialValues={{
              referenceStatus: selectedReference.referenceStatus ?? 0,
            }}
            validationSchema={validationSchema}
            onSubmit={(values, { resetForm }) => handleUpdate(values, resetForm)}
            enableReinitialize
          >
            {() => (
              <Form>
                <section className="fields">
                  <Select
                    name="referenceStatus"
                    label="Reference Status"
                    placeholder="Select status"
                    options={[
                      <option key="0" value={0}>Pending</option>,
                      <option key="1" value={1}>Sent To Referee</option>,
                      <option key="2" value={2}>Received Reference Letter</option>,
                    ]}
                  />
                  <Flex justify="flex-end" gap="12px" className={styles.verifyActions}>
                    <AntButton className={styles.modalActionButton} onClick={() => setSelectedReference(null)}>
                      Cancel
                    </AntButton>
                    <Button
                      className={styles.modalActionButton}
                      type="submit"
                      text="Update"
                      isLoading={updateStatusMutation.isPending}
                      disabled={updateStatusMutation.isPending}
                    />
                  </Flex>
                </section>
              </Form>
            )}
          </Formik>
        )}
      </Modal>
    </>
  );
};

export default ViewApplicant;
